import { Injectable } from '@angular/core';
import { UserActivityService } from './user-activity.service';
import { AuthGaurdService } from '../service/auth-gaurd.service';

@Injectable({
  providedIn: 'root'
})
export class SessionInitService {
  private readonly SESSION_STORAGE_PREFIX = 'app_session_';
  private readonly BROWSER_SESSION_KEY = 'browser_session_id';

  constructor(
    private userActivityService: UserActivityService,
    private authGuardService: AuthGaurdService
  ) {}

  /**
   * Initialize session management for the application
   * This should be called once on application startup
   */
  initializeSession(): void {
    const existingBrowserSession = sessionStorage.getItem(this.BROWSER_SESSION_KEY);
    
    if (!existingBrowserSession) {
      // New browser session - generate new session ID
      const newSessionId = this.generateSessionId();
      sessionStorage.setItem(this.BROWSER_SESSION_KEY, newSessionId);
      this.cleanupPreviousSessionData();
    } else {
      // Existing browser session - restore if valid
      this.validateAndRestoreSession();
    }
  }

  /**
   * Check if the current session is valid
   */
  isValidSession(): boolean {
    const sessionId = sessionStorage.getItem(this.BROWSER_SESSION_KEY);
    const token = this.authGuardService.getToken();
    
    return !!(sessionId && token && !this.isSessionExpired());
  }

  /**
   * Clear all session data for the current application
   */
  clearApplicationSession(): void {
    // Clear sessionStorage items
    sessionStorage.removeItem(this.BROWSER_SESSION_KEY);
    sessionStorage.removeItem('auth-token');
    sessionStorage.removeItem('auth-user');

    // Clear application-specific localStorage items
    const appKeys = [
      'session-state',
      'last-activity', 
      'user-activity',
      'NOOOO',
      'token'
    ];

    appKeys.forEach(key => localStorage.removeItem(key));
  }

  /**
   * Restore session state from storage if valid
   */
  private validateAndRestoreSession(): void {
    const token = this.authGuardService.getToken();
    
    if (!token || this.authGuardService.isTokenExpired(token)) {
      // Session is invalid, start fresh
      this.clearApplicationSession();
      this.initializeSession();
      return;
    }

       // Check if returning after long inactivity (e.g., next day)
    const lastActivity = localStorage.getItem('last-activity');
    if (lastActivity) {
      const timeSinceActivity = Date.now() - parseInt(lastActivity, 10);
      // If inactive for more than 30 minutes, silently clear expired state and reset
      if (timeSinceActivity > (30 * 60 * 1000)) {
        localStorage.removeItem('session-state'); // Clear expired/warning state
        this.userActivityService.extendSession(); // Reset activity to now
        return;
      }
    }

    // Session is valid - ensure user activity service is properly initialized
    if (!this.userActivityService.isUserActive()) {
      // Reset activity to current time to prevent immediate timeout
      this.userActivityService.extendSession();
    }
  }

  /**
   * Check if session has expired based on activity
   */
  private isSessionExpired(): boolean {
    const lastActivity = localStorage.getItem('last-activity');
    if (!lastActivity) return true;

    const timeSinceActivity = Date.now() - parseInt(lastActivity, 10);
    return timeSinceActivity > (60 * 60 * 1000); // 60 minutes
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
  }

  /**
   * Clean up any stale session data from previous browser sessions
   */
  private cleanupPreviousSessionData(): void {
    // Clear localStorage items that should not persist across browser sessions
    localStorage.removeItem('session-state');
    localStorage.removeItem('last-activity');
    localStorage.removeItem('user-activity');
  }

  /**
   * Get current browser session ID
   */
  getCurrentSessionId(): string | null {
    return sessionStorage.getItem(this.BROWSER_SESSION_KEY);
  }

  /**
   * Check if this is a new browser session
   */
  isNewBrowserSession(): boolean {
    return !sessionStorage.getItem(this.BROWSER_SESSION_KEY);
  }
}